<template>
    <div class="row flex-grow q-mx-sm">
        <div v-if="!hideFacets">
            <QitBtn
                color="white"
                size="13px"
                padding="sm"
                @click="showPopup"
                :icon="
                    selectedFacetsCount > 0
                        ? 'fa-regular fa-filter primary-color'
                        : 'fa-regular fa-filter default-text-color'
                "
                :label="
                    $t('shared.Filter{activeFilterCount}', {
                        activeFilterCount: selectedFacetsCount > 0 ? ` (${selectedFacetsCount})` : '',
                    })
                "
                data-qs="facet-modal-open-btn-mobile"
            />
        </div>
        <div class="sorting-container row flex-grow justify-end items-center">
            <ListActions
                :show-grouping="false"
                :grouping-save-key="groupingSaveKey"
                :more-menu-entries="moreMenuEntries"
                :sort-order-entries="sortOrderEntries"
                :initial-selected-sort-entry="initialSelectedSortEntry"
                :sort-save-key="sortSaveKey"
                @on-sort-selection-changed="onSortSelectionChanged"
            />
        </div>
        <q-dialog v-model="popupVisible" @update:model-value="popupVisibleUpdate" :maximized="isXSmall">
            <q-card
                :class="['filter-dialog-card column fit', isXSmall ? '' : 'card-Large']"
                data-qs="list-filter-dialog"
            >
                <div class="align-center col-auto">
                    <div class="q-pa-md row">
                        <div class="col-grow text-weight-bold text-h6">
                            <i class="fa-regular fa-filter default-text-color q-mr-xs" />
                            <template v-if="title">{{ title }}</template>
                            <template v-else>{{ $t("shared.Filter search results") }}</template>
                        </div>
                        <div>
                            <i
                                class="fa-regular fa-xmark icon-color cursor-pointer"
                                @click="hidePopup()"
                                data-qs="facet-modal-close-btn-mobile"
                                style="font-size: 22px"
                            />
                        </div>
                    </div>
                    <q-separator />
                </div>
                <div v-if="showGrouping && groupingSaveKey" class="q-mt-sm">
                    <PersistentGroupingToggle :grouping-save-key="groupingSaveKey" />
                </div>
                <q-scroll-area class="col-grow q-pa-md">
                    <div v-for="facet in facets" :key="facet.id" class="q-mb-sm">
                        <FacetDropdown
                            v-bind="getFacetDropdownProps(facet)"
                            @on-value-changed="onValueChangedHandler"
                            @on-open-filter="onOpenFilterHandler"
                        />
                    </div>
                </q-scroll-area>
                <div class="col-auto">
                    <q-separator />
                    <div class="row justify-between q-pa-sm full-width">
                        <q-btn
                            no-caps
                            unelevated
                            color="grey-2"
                            text-color="on-link-color"
                            :label="$t('shared.Reset filter')"
                            @click="deleteFilterSelection"
                            :disabled="disableDeleteButton"
                        />
                        <q-btn
                            no-caps
                            unelevated
                            color="primary"
                            :label="$t('shared.Show results')"
                            @click="hidePopup"
                            data-qs="show-result-btn"
                        />
                    </div>
                </div>
            </q-card>
        </q-dialog>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRefs, watch } from "vue";
import { Facet, FacetSelected } from "@/shared/facets/facets.model";
import FacetDropdown from "@/shared/facets/dropdown/facet-dropdown.vue";
import QitBtn from "@/shared/components/buttons/qit-button.vue";
import { useScreenSize } from "@/shared/screen/composables/screen-size";
import { SortEntry } from "@/shared/components/sort-dropdown/sort-dropdown.model";
import ListActions from "@/shared/list-filter/list-actions.vue";
import PersistentGroupingToggle from "@/shared/list-filter/persistent-grouping-toggle.vue";
import type { ListActionsProps, ListFacetProps } from "@/shared/list-filter/list-filter.types";

const props = defineProps<
    ListFacetProps &
        ListActionsProps & {
            isMobileView: boolean;
            title?: string;
        }
>();

const emits = defineEmits<{
    onResetSelection: [];
    onValueChanged: [facetSelected: FacetSelected];
    onSortSelectionChanged: [sortEntry: SortEntry];
    onOpenFilter: [facet: Facet];
}>();

const { isXSmall } = useScreenSize();

const { disableDeleteButton, selected, sortOrderEntries, moreMenuEntries } = toRefs(props);
const popupVisible = ref<boolean>(false);

const onValueChangedHandler = (facetSelected: FacetSelected) => {
    emits("onValueChanged", facetSelected);
};

const onOpenFilterHandler = (facet: Facet) => {
    emits("onOpenFilter", facet);
};

//TODO remove isMobileView as prop and use useScreenSize instead
//close popup if screen sizes isn't xs anymore
watch(
    () => props.isMobileView,
    (val) => {
        if (!val) popupVisible.value = false;
    }
);

const popupVisibleUpdate = () => {
    showPopup();
};

const selectedFacetsCount = computed(() => {
    return selected.value.reduce((accumulator, val) => accumulator + val.values.length, 0);
});

const showPopup = () => {
    popupVisible.value = true;
};

const hidePopup = () => {
    popupVisible.value = false;
};

const deleteFilterSelection = () => {
    emits("onResetSelection");
};

const onSortSelectionChanged = (sortEntry: SortEntry) => {
    emits("onSortSelectionChanged", sortEntry);
};
</script>

<style lang="scss" scoped>
.filter-dialog-card {
    height: 95% !important;

    &:deep(.facet-dropdown) {
        width: 100%;
        max-width: 100%;
    }
}

.card-Large {
    min-width: 498px;
}
</style>
